/**
 * Hook to check if current user has access to employee documents
 */
import { useState, useEffect } from 'react';
import { ref, get } from 'firebase/database';
import { database } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

export const useDocumentAccess = (companyUID, employeeId) => {
  const { currentUser } = useAuth();
  const [hasAccess, setHasAccess] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAccess = async () => {
      if (!currentUser || !companyUID || !employeeId) {
        setHasAccess(false);
        setLoading(false);
        return;
      }

      try {
        const userUID = currentUser.uid;
        let role = null;
        let access = false;

        // Check 1: Is user the company owner?
        if (userUID === companyUID) {
          access = true;
          role = 'owner';
        } else {
          // Check 2: Is user a collaborator?
          try {
            const collabRef = ref(database, `collaborators/${userUID}`);
            const collabSnap = await get(collabRef);
            
            if (collabSnap.exists()) {
              const collabData = collabSnap.val();
              if (collabData.companyId === companyUID) {
                access = true;
                role = 'collaborator';
              }
            }
          } catch (error) {
            console.warn('Error checking collaborator status:', error);
          }

          // Check 3: Is user the employee themselves?
          if (!access && userUID === employeeId) {
            access = true;
            role = 'employee';
          } else if (!access) {
            // Check by email match
            try {
              const empRef = ref(database, `companies/${companyUID}/employees/${employeeId}`);
              const empSnap = await get(empRef);
              
              if (empSnap.exists()) {
                const empData = empSnap.val();
                const profile = empData.profile || {};
                const contact = empData.contact || {};
                
                const employeeEmail = (
                  profile.workEmail || 
                  contact.workEmail || 
                  contact.email || 
                  profile.email
                );
                
                if (employeeEmail && currentUser.email && 
                    employeeEmail.toLowerCase() === currentUser.email.toLowerCase()) {
                  access = true;
                  role = 'employee';
                }
              }
            } catch (error) {
              console.warn('Error checking employee email match:', error);
            }
          }
        }

        setHasAccess(access);
        setUserRole(role);
      } catch (error) {
        console.error('Error checking document access:', error);
        setHasAccess(false);
      } finally {
        setLoading(false);
      }
    };

    checkAccess();
  }, [currentUser, companyUID, employeeId]);

  return { hasAccess, userRole, loading };
};

export default useDocumentAccess;





